import React, { forwardRef } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { COLOURS, KIND, SOURCE, chipSortId } from './chipTypes'

export const chipLabel = (kind, chip) => {
  if (kind === KIND.CONSUMABLE) {
    const name = chip?.name?.trim() || 'Unnamed consumable'
    return chip?.size?.trim() ? `${name} · ${chip.size}` : name
  }
  if (kind === KIND.ASSET_WILDCARD) return `Any ${chip?.category || 'asset'}`
  return chip?.subCategory || chip?.category || 'Asset'
}

// Pure presentational chip. `handleProps` receives the dnd-kit listeners so
// only the dots start a drag and a plain click on the body selects it.
const ItemChip = forwardRef(function ItemChip(
  {
    kind,
    chip,
    selected = false,
    dragging = false,
    overlay = false,
    readOnly = false,
    handleProps = {},
    onClick,
    style,
    sx,
    ...rest
  },
  ref,
) {
  const isAsset = kind !== KIND.CONSUMABLE
  const isWildcard = kind === KIND.ASSET_WILDCARD
  const qty = Number(chip?.quantity) || 1

  return (
    <Stack
      ref={ref}
      direction="row"
      alignItems="center"
      onClick={onClick}
      style={style}
      {...rest}
      sx={{
        pl: readOnly ? 1 : 0.25,
        pr: 0.5,
        py: 0.25,
        gap: 0.5,
        borderRadius: '5px',
        border: '1px solid',
        borderStyle: isWildcard ? 'dashed' : 'solid',
        borderColor: isAsset ? COLOURS.assetBorder : COLOURS.divider,
        bgcolor: isAsset ? COLOURS.assetBg : 'background.paper',
        boxShadow: overlay
          ? '0 6px 18px rgba(36,47,64,0.25)'
          : selected
            ? `0 0 0 2px ${COLOURS.teal}`
            : 'none',
        opacity: dragging ? 0.35 : 1,
        cursor: onClick ? 'pointer' : 'default',
        maxWidth: '100%',
        userSelect: 'none',
        ...sx,
      }}
    >
      {!readOnly && (
        <Box
          {...handleProps}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.disabled',
            cursor: 'grab',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
          aria-label="Drag"
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontSize: '0.8rem',
          color: isAsset ? COLOURS.navy : 'text.primary',
          fontStyle: isWildcard ? 'italic' : 'normal',
        }}
      >
        {chipLabel(kind, chip)}
      </Typography>
      <Box
        component="span"
        sx={{
          ml: 0.25,
          px: 0.75,
          borderRadius: '999px',
          bgcolor: COLOURS.navy,
          color: '#fff',
          fontSize: '0.68rem',
          fontWeight: 700,
          lineHeight: '18px',
          whiteSpace: 'nowrap',
        }}
      >
        ×{qty}
      </Box>
    </Stack>
  )
})

// Sortable wrapper used inside a section's SortableContext.
export const SortableItemChip = ({
  sectionId,
  kind,
  chip,
  selected,
  readOnly,
  onSelect,
}) => {
  const id = chipSortId(sectionId, chip.clientId)
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: readOnly,
    data: {
      source: SOURCE.SECTION,
      kind,
      sectionId,
      chipId: chip.clientId,
      chip,
    },
  })

  return (
    <ItemChip
      ref={setNodeRef}
      kind={kind}
      chip={chip}
      selected={selected}
      dragging={isDragging}
      readOnly={readOnly}
      onClick={() => onSelect?.({ sectionId, chipId: chip.clientId })}
      handleProps={{ ...attributes, ...listeners, ref: setActivatorNodeRef }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    />
  )
}

export default ItemChip
